import { useCallback, useState, type ReactNode } from "react";
import { css } from "styled-system/css";

interface TipState {
  x: number;
  y: number;
  content: ReactNode;
}

/**
 * Hover tooltip for SVG figures. `bind(content)` returns the handlers to
 * spread on a mark; render `node` once inside the figure.
 */
export function useTooltip() {
  const [tip, setTip] = useState<TipState | null>(null);

  const bind = useCallback(
    (content: ReactNode) => ({
      onMouseMove: (e: React.MouseEvent) =>
        setTip({ x: e.clientX, y: e.clientY, content }),
      onMouseLeave: () => setTip(null),
      onTouchStart: (e: React.TouchEvent) => {
        const t = e.touches[0];
        if (t) setTip({ x: t.clientX, y: t.clientY, content });
      },
    }),
    [],
  );

  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const node = tip ? (
    <div
      role="tooltip"
      className={tipClass}
      style={{
        left: Math.min(tip.x + 14, vw - 268),
        top: Math.min(tip.y + 14, vh - 120),
      }}
    >
      {tip.content}
    </div>
  ) : null;

  return { bind, node };
}

const tipClass = css({
  position: "fixed",
  pointerEvents: "none",
  zIndex: 50,
  maxWidth: "260px",
  bg: "ed.paper",
  color: "ed.ink",
  border: "1px solid",
  borderColor: "ed.ink",
  px: "10px",
  py: "7px",
  fontFamily: "sans",
  fontSize: "12.5px",
  lineHeight: "1.4",
  "& b": { display: "block", fontWeight: 700, mb: "2px" },
  "& .num": { fontFamily: "mono", fontVariantNumeric: "tabular-nums" },
});
